import React from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import { ToggleButton, ToggleButtonGroup } from '@mui/lab'
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import EventIcon from '@mui/icons-material/Event'
import GridViewIcon from '@mui/icons-material/GridView'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type { Dayjs } from 'dayjs'
import QuarterDatePicker from '../../utilities/quarterDatePicker'
import { requiredFieldHelperText } from './requiredFieldHelperText'
import CaretDownSvg from '../../assets/images/CaretDown.svg'

type DateMode = 'month' | 'quarter'

const FieldLabel: React.FC<{ label: string; tooltip: string; required?: boolean }> = ({ label, tooltip, required }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="16x600">
            {label}
            {required && (
                <Typography component="span" sx={{ color: 'error.main' }}>
                    {' '}
                    *
                </Typography>
            )}
        </Typography>
        <Tooltip title={tooltip} arrow>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'default' }} />
        </Tooltip>
    </Box>
)

interface ActivityDateFieldProps {
    mode: DateMode
    onModeChange: (mode: DateMode) => void

    // Quarter actual dates — oldStart/oldEnd are the committed values passed to QuarterDatePicker for display
    quarterOldStart: string | null
    quarterOldEnd: string | null
    onQuarterStartChange: (date: string) => void
    onQuarterEndChange: (date: string) => void

    // Calendar actual dates
    startDate: Dayjs | null
    endDate: Dayjs | null
    onStartDateChange: (date: Dayjs | null) => void
    onEndDateChange: (date: Dayjs | null) => void

    // Planned dates
    plannedStartDate: Dayjs | null
    plannedEndDate: Dayjs | null
    onPlannedStartDateChange: (date: Dayjs | null) => void
    onPlannedEndDateChange: (date: Dayjs | null) => void

    // Study milestone dates
    studyReportDate: Dayjs | null
    onStudyReportDateChange: (date: Dayjs | null) => void
    terminationDate: Dayjs | null
    onTerminationDateChange: (date: Dayjs | null) => void
    resultsAvailableForExternalUseDate: Dayjs | null
    onResultsAvailableForExternalUseDateChange: (date: Dayjs | null) => void

    disabled?: boolean
    // Shows a clear button next to each actual date picker
    clearable?: boolean
    // Keyed by the org's required-fields config (e.g. 'startDate', 'plannedStartDate', 'studyReportDate')
    requiredFields?: string[]
    fieldErrors?: Record<string, string>
}

const ActivityDateField: React.FC<ActivityDateFieldProps> = ({
    mode,
    onModeChange,
    quarterOldStart,
    quarterOldEnd,
    onQuarterStartChange,
    onQuarterEndChange,
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    plannedStartDate,
    plannedEndDate,
    onPlannedStartDateChange,
    onPlannedEndDateChange,
    studyReportDate,
    onStudyReportDateChange,
    terminationDate,
    onTerminationDateChange,
    resultsAvailableForExternalUseDate,
    onResultsAvailableForExternalUseDateChange,
    disabled,
    clearable,
    requiredFields,
    fieldErrors,
}) => {
    const [expanded, setExpanded] = React.useState(true)
    const [startError, setStartError] = React.useState<string | null>(null)
    const [endError, setEndError] = React.useState<string | null>(null)

    // Auto-expand this section when a failed submit flags one of its fields, so a missing required
    // field can't hide inside a collapsed section. Keyed on a stable boolean so the effect only
    // fires on the false→true transition — it won't re-open a section the user collapses while the
    // error is still unresolved.
    const hasMissingField = [
        'startDate',
        'endDate',
        'plannedStartDate',
        'plannedEndDate',
        'studyReportDate',
        'terminationDate',
        'resultsAvailableForExternalUseDate',
    ].some((f) => !!fieldErrors?.[f])
    React.useEffect(() => {
        if (hasMissingField) setExpanded(true)
    }, [hasMissingField])

    const startErrorMessage = React.useMemo(() => {
        if (startError === 'invalidDate') return 'Invalid date'
        return ''
    }, [startError])

    const endErrorMessage = React.useMemo(() => {
        if (endError === 'minDate') return 'End is before Start'
        if (endError === 'invalidDate') return 'Invalid date'
        return ''
    }, [endError])

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            disableGutters
            elevation={0}
            sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}
        >
            <AccordionSummary
                expandIcon={
                    <Box
                        component="img"
                        src={CaretDownSvg}
                        sx={{
                            width: 20,
                            height: 20,
                            transition: 'transform 0.2s',
                            transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
                        }}
                    />
                }
                sx={{
                    px: 0,
                    py: 1,
                    backgroundColor: 'inherit',
                    '& .MuiAccordionSummary-expandIconWrapper': { transform: 'rotate(0deg)' },
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(180deg)' },
                }}
            >
                <Typography variant="18x600">Dates</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <ToggleButtonGroup
                            value={mode}
                            exclusive
                            onChange={(_, newMode: DateMode | null) => {
                                if (newMode !== null) onModeChange(newMode)
                            }}
                            aria-label="Quarters or Months"
                            color="primary"
                            disabled={disabled}
                        >
                            <ToggleButton value="month" aria-label="month">
                                <Tooltip title="Specific Date">
                                    <EventIcon />
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="quarter" aria-label="quarter">
                                <Tooltip title="Quarterly Estimate">
                                    <GridViewIcon />
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {mode === 'quarter' ? (
                        <QuarterDatePicker
                            oldStart={quarterOldStart}
                            oldEnd={quarterOldEnd}
                            start={onQuarterStartChange}
                            end={onQuarterEndChange}
                            disabled={disabled}
                            startRequired={requiredFields?.includes('startDate')}
                            endRequired={requiredFields?.includes('endDate')}
                            startFieldError={fieldErrors?.startDate}
                            endFieldError={fieldErrors?.endDate}
                        />
                    ) : (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <FieldLabel
                                    label="Actual Start Date"
                                    tooltip="The activity's actual initiation date or the first patient's first visit."
                                    required={requiredFields?.includes('startDate')}
                                />
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                        <MUIDatePicker
                                            value={startDate}
                                            onChange={onStartDateChange}
                                            onError={(e) => setStartError(e)}
                                            disabled={disabled}
                                            slotProps={{
                                                textField: {
                                                    error: !!fieldErrors?.startDate,
                                                    helperText:
                                                        fieldErrors?.startDate ||
                                                        startErrorMessage ||
                                                        (requiredFields?.includes('startDate') ? 'Required' : ' '),
                                                    fullWidth: true,
                                                },
                                            }}
                                            sx={{ width: '100%' }}
                                        />
                                        {clearable && startDate && (
                                            <IconButton
                                                onClick={() => onStartDateChange(null)}
                                                size="small"
                                                sx={{ ml: 1, alignSelf: 'flex-start' }}
                                            ></IconButton>
                                        )}
                                    </Box>
                                </LocalizationProvider>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FieldLabel
                                    label="Actual End Date"
                                    tooltip="The activity's actual completion date or the last patient's last visit."
                                    required={requiredFields?.includes('endDate')}
                                />
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                        <MUIDatePicker
                                            value={endDate}
                                            onChange={onEndDateChange}
                                            minDate={startDate ?? undefined}
                                            onError={(e) => setEndError(e)}
                                            disabled={disabled}
                                            slotProps={{
                                                textField: {
                                                    error: !!fieldErrors?.endDate,
                                                    helperText:
                                                        fieldErrors?.endDate ||
                                                        endErrorMessage ||
                                                        (requiredFields?.includes('endDate') ? 'Required' : ' '),
                                                    fullWidth: true,
                                                },
                                            }}
                                            sx={{ width: '100%' }}
                                        />
                                        {clearable && endDate && (
                                            <IconButton
                                                onClick={() => onEndDateChange(null)}
                                                size="small"
                                                sx={{ ml: 1, alignSelf: 'flex-start' }}
                                            ></IconButton>
                                        )}
                                    </Box>
                                </LocalizationProvider>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <FieldLabel
                                label="Planned Start Date"
                                tooltip="The expected date the activity is planned to begin (e.g., study initiation or first patient's first visit)."
                                required={requiredFields?.includes('plannedStartDate')}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <MUIDatePicker
                                    value={plannedStartDate}
                                    onChange={onPlannedStartDateChange}
                                    disabled={disabled}
                                    slotProps={{
                                        textField: {
                                            error: !!fieldErrors?.plannedStartDate,
                                            helperText: requiredFieldHelperText(
                                                'plannedStartDate',
                                                requiredFields,
                                                fieldErrors
                                            ),
                                            fullWidth: true,
                                        },
                                    }}
                                    sx={{ width: '100%' }}
                                />
                            </LocalizationProvider>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <FieldLabel
                                label="Planned End Date"
                                tooltip="The expected date the activity is planned to conclude (e.g., study completion or last patient's last visit)."
                                required={requiredFields?.includes('plannedEndDate')}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <MUIDatePicker
                                    value={plannedEndDate}
                                    onChange={onPlannedEndDateChange}
                                    minDate={plannedStartDate ?? undefined}
                                    disabled={disabled}
                                    slotProps={{
                                        textField: {
                                            error: !!fieldErrors?.plannedEndDate,
                                            helperText: requiredFieldHelperText(
                                                'plannedEndDate',
                                                requiredFields,
                                                fieldErrors
                                            ),
                                            fullWidth: true,
                                        },
                                    }}
                                    sx={{ width: '100%' }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <FieldLabel
                                label="FSR Actual Date"
                                tooltip="The date the Full Study Report (FSR) was finalized and completed."
                                required={requiredFields?.includes('studyReportDate')}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <MUIDatePicker
                                    value={studyReportDate}
                                    onChange={onStudyReportDateChange}
                                    disabled={disabled}
                                    slotProps={{
                                        textField: {
                                            error: !!fieldErrors?.studyReportDate,
                                            helperText: requiredFieldHelperText(
                                                'studyReportDate',
                                                requiredFields,
                                                fieldErrors
                                            ),
                                            fullWidth: true,
                                        },
                                    }}
                                    sx={{ width: '100%' }}
                                />
                            </LocalizationProvider>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <FieldLabel
                                label="Terminated On Date"
                                tooltip="The date the activity was terminated, canceled, or otherwise discontinued."
                                required={requiredFields?.includes('terminationDate')}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <MUIDatePicker
                                    value={terminationDate}
                                    onChange={onTerminationDateChange}
                                    disabled={disabled}
                                    slotProps={{
                                        textField: {
                                            error: !!fieldErrors?.terminationDate,
                                            helperText: requiredFieldHelperText(
                                                'terminationDate',
                                                requiredFields,
                                                fieldErrors
                                            ),
                                            fullWidth: true,
                                        },
                                    }}
                                    sx={{ width: '100%' }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <FieldLabel
                                label="Results Available for External Use"
                                tooltip="The date the activity's results became available for external use."
                                required={requiredFields?.includes('resultsAvailableForExternalUseDate')}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <MUIDatePicker
                                    value={resultsAvailableForExternalUseDate}
                                    onChange={onResultsAvailableForExternalUseDateChange}
                                    disabled={disabled}
                                    slotProps={{
                                        textField: {
                                            error: !!fieldErrors?.resultsAvailableForExternalUseDate,
                                            helperText: requiredFieldHelperText(
                                                'resultsAvailableForExternalUseDate',
                                                requiredFields,
                                                fieldErrors
                                            ),
                                            fullWidth: true,
                                        },
                                    }}
                                    sx={{ width: '100%' }}
                                />
                            </LocalizationProvider>
                        </Box>
                        <Box sx={{ flex: 1 }} />
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}

export default ActivityDateField
